import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { cartCount } from '@theme/cart-count';
import { parsePlanTiers, nearestPlan } from '@theme/selling-plans';
import { enqueueCartMutation } from '@theme/cart-queue';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** True when this drawer was empty and we swapped in a pending filled shell. */
  #optimisticFromEmpty = false;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    if (this.isDrawer) {
      cartCount.seed(this.#renderedDrawerCount());
      cartCount.addEventListener(cartCount.changeEvent, this.#renderDrawerCount);
    }

    // A cart built before the plans were tiered (or edited elsewhere) can
    // hold lines on the wrong tier — settle it once the rows are collected.
    queueMicrotask(() => this.reconcilePlans());

    // Back from the checkout (or any page) restores this page from the
    // back/forward cache with the cart as it was: a quantity edited elsewhere,
    // or a checkout that moved on, isn't reflected. Re-render from the server.
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('pageshow', this.#handlePageShow);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    cartCount.removeEventListener(cartCount.changeEvent, this.#renderDrawerCount);
    this.#debouncedOnChange.cancel();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.#rowForLine(line);

    // A row can stand for a line Shopify shows split in two (a Buy X Get Y
    // gift discount pins one unit on its own line). The stepper shows the
    // combined count; this line only owns the part above the offset.
    const offset = parseInt(lineItemRow?.dataset.quantityOffset ?? '', 10) || 0;
    const lineQuantity = Math.max(0, quantity - offset);

    if (lineQuantity === 0 && offset === 0) {
      return this.onLineItemRemove(line);
    }

    // Subscription plans are tiered by item count: the new quantity may move
    // the line to the plan of another tier at the same cadence. Sending the
    // plan with the quantity saves a second round trip through reconcilePlans,
    // and the split siblings of the line follow it in the same request chain.
    const sellingPlan = lineItemRow && lineQuantity > 0 ? this.#planForQuantity(lineItemRow, quantity) : null;
    if (sellingPlan && lineItemRow) this.#noteCadenceChange(lineItemRow, sellingPlan);

    this.updateQuantity({
      line,
      quantity: lineQuantity,
      action: 'change',
      ...(sellingPlan && lineItemRow
        ? { sellingPlan, followUps: this.#groupFollowUps(lineItemRow, sellingPlan) }
        : {}),
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * The selling plan a row lands on once its group quantity becomes
   * `quantity`: the tier for everything shipping at its cadence, at that
   * cadence — or the nearest one the tier offers (three packs ship every 60
   * days, one pack every 15 to 45; cut to one, the line can't keep 60).
   * Null when the row already sits on that plan, or has no tier map.
   *
   * @param {HTMLElement} row
   * @param {number} quantity - The new quantity of the row's line group
   * @returns {number | null}
   */
  #planForQuantity(row, quantity) {
    const entries = parsePlanTiers(row.dataset.planTiers);
    const ord = Number(row.dataset.planOrd);
    const currentId = Number(row.dataset.planId);
    if (entries.length === 0 || !Number.isFinite(ord) || !currentId) return null;

    // Every line at this cadence counts; the row's own group is replaced by
    // the new quantity (merged sibling lines make up the offset).
    const ownGroup = (Number(row.dataset.lineQuantity) || 0) + (Number(row.dataset.quantityOffset) || 0);
    const cadenceTotal = this.#subscriptionRows()
      .filter((other) => Number(other.dataset.planOrd) === ord)
      .reduce((sum, other) => sum + (Number(other.dataset.lineQuantity) || 0), 0);

    const target = nearestPlan(entries, cadenceTotal - ownGroup + quantity, ord);
    return target && target.id !== currentId ? target.id : null;
  }

  /**
   * Cadence swaps waiting to be named on their rows once the cart has
   * re-rendered, by the variant and plan the line moves to. The row is
   * replaced by the render (a plan change rewrites the line key), so the
   * note outlives it here.
   * @type {Map<string, { from: string }>}
   */
  #cadenceNotices = new Map();

  /**
   * Remembers that moving `row` to `sellingPlan` changes its delivery
   * frequency, so the swap can be spelled out on the row after the render.
   *
   * @param {HTMLElement} row
   * @param {number} sellingPlan
   */
  #noteCadenceChange(row, sellingPlan) {
    const entries = parsePlanTiers(row.dataset.planTiers);
    const target = entries.find((entry) => entry.id === sellingPlan);
    const fromOrd = Number(row.dataset.planOrd);
    if (!target || !Number.isFinite(fromOrd) || target.ord === fromOrd) return;

    this.#cadenceNotices.set(`${row.dataset.variantId}:${sellingPlan}`, { from: row.dataset.planCadence ?? '' });
    this.#captureDiagnostic('cart_plan_cadence_changed', {
      variant_id: Number(row.dataset.variantId) || undefined,
      from_plan_id: Number(row.dataset.planId) || undefined,
      to_plan_id: sellingPlan,
      from_days: fromOrd,
      to_days: target.ord,
    });
  }

  /**
   * Names the cadence swaps recorded for rows now in the cart: "Delivery
   * updated to every 2 weeks …" under the row's frequency picker. A row
   * shows its note once; the next render clears it.
   */
  #showCadenceNotices() {
    if (this.#cadenceNotices.size === 0) return;
    for (const row of this.refs.cartItemRows ?? []) {
      const key = `${row.dataset.variantId}:${row.dataset.planId}`;
      const notice = this.#cadenceNotices.get(key);
      const element = row.querySelector('[data-ref="planNotice"]');
      if (!notice || !(element instanceof HTMLElement)) continue;

      const to = row.dataset.planCadence ?? '';
      if (notice.from && to) {
        element.textContent = (element.dataset.template ?? '').replace('[from]', notice.from).replace('[to]', to);
        element.hidden = false;
      }
      this.#cadenceNotices.delete(key);
    }
  }

  /**
   * A safe, aggregate diagnostic for PostHog (no customer input): why a
   * cart ended up different from what the shopper picked. Queued on the
   * stub until consent initializes the SDK; dropped without consent.
   *
   * @param {string} name
   * @param {Record<string, unknown>} properties
   */
  #captureDiagnostic(name, properties) {
    try {
      /** @type {{ capture?: (name: string, properties: Record<string, unknown>) => void } | undefined} */
      const posthog = /** @type {any} */ (window).posthog;
      posthog?.capture?.(name, { source: 'storefront_theme', ...properties });
    } catch {
      // Analytics never interrupts the cart
    }
  }

  /**
   * Re-renders the cart from the server after a back/forward-cache restore.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    enqueueCartMutation(async () => {
      try {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } catch (error) {
        console.warn('[cart-items] Cart refresh after restore failed:', error);
        return;
      }
      if (this.isDrawer) cartCount.seed(this.#renderedDrawerCount());
      this.#renderDrawerCount();
      this.#updateCartQuantitySelectorButtonStates();
    }).then(() => this.reconcilePlans());
  };

  /**
   * The other rows of a line Shopify shows split in two (same variant, plan
   * and properties — a Buy X Get Y gift discount pins one unit on its own
   * line), each with the quantity it owns.
   *
   * @param {HTMLElement} row
   * @param {number} sellingPlan - The plan the whole group moves to
   * @returns {Array<{ key: string, quantity: number, sellingPlan: number }>}
   */
  #groupFollowUps(row, sellingPlan) {
    const groupKey = row.dataset.groupKey;
    if (!groupKey) return [];
    return (this.refs.cartItemRows ?? [])
      .filter((other) => other !== row && other.dataset.groupKey === groupKey && !other.classList.contains('removing'))
      .map((other) => ({
        key: other.dataset.key ?? '',
        quantity: Number(other.dataset.lineQuantity) || 0,
        sellingPlan,
      }))
      .filter((followUp) => followUp.key && followUp.quantity > 0);
  }

  /** Section ids of every cart-items component on the page, for `sections` requests */
  get #cartSectionIds() {
    const ids = new Set([this.sectionId]);
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return [...ids].join(',');
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} body
   * @returns {Promise<any>} The parsed JSON response
   */
  async #postCart(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    return response.json();
  }

  /**
   * The row of a cart line. The rows list the gifts after the products, so
   * a line's row isn't at the line's position: each carries its line number.
   *
   * @param {number} line - The 1-based line index.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    return (this.refs.cartItemRows ?? []).find((row) => Number(row.dataset.line) === line);
  }

  /**
   * Subscription rows still in the cart. Only they carry a cadence; a
   * one-time row can carry a tier map too (for its upgrade offer), and a
   * gift line carries neither.
   */
  #subscriptionRows() {
    return (this.refs.cartItemRows ?? []).filter(
      (row) => row.dataset.planOrd !== undefined && !row.classList.contains('removing')
    );
  }

  /**
   * Moves every subscription line whose plan no longer matches its tier.
   *
   * The server render marks each row with the plan it should be on
   * (`data-plan-target`, from the quantity shipping at its cadence); a
   * quantity change, a removal or a cadence switch can leave OTHER lines on
   * the wrong tier, and a cart built before the tiers existed can be wrong
   * from the start. Lines are moved one request at a time by key (a plan
   * change rewrites the line key), only the last request renders the
   * sections, and the whole pass waits its turn in the cart mutation queue
   * so it never interleaves with the gift sync.
   */
  reconcilePlans() {
    return enqueueCartMutation(() => this.#reconcile());
  }

  async #reconcile() {
    const sections = this.#cartSectionIds;
    this.#showCadenceNotices();

    // A move keeps the cadence (or takes the nearest one its tier offers,
    // which the server resolved from the same totals), so one pass settles
    // the cart; the second only retries a rejected move.
    for (let pass = 0; pass < 2; pass++) {
      const moves = this.#subscriptionRows()
        .map((row) => ({
          row,
          key: row.dataset.key ?? '',
          quantity: Number(row.dataset.lineQuantity) || 0,
          sellingPlan: Number(row.dataset.planTarget) || 0,
          current: Number(row.dataset.planId) || 0,
        }))
        .filter((move) => move.key && move.quantity > 0 && move.sellingPlan && move.sellingPlan !== move.current);
      if (moves.length === 0) return;
      for (const move of moves) this.#noteCadenceChange(move.row, move.sellingPlan);

      this.#disableCartItems();
      let lastResponse = null;
      try {
        for (const [index, move] of moves.entries()) {
          const body = { id: move.key, quantity: move.quantity, selling_plan: move.sellingPlan };
          const parsed = await this.#postCart(
            Theme.routes.cart_change_url,
            index === moves.length - 1 ? { ...body, sections, sections_url: window.location.pathname } : body
          );
          if (parsed.errors) break;
          lastResponse = parsed;
        }
      } catch (error) {
        console.warn('[cart-items] Plan reconcile failed:', error);
      } finally {
        this.#enableCartItems();
      }

      if (!lastResponse) return;
      await this.#renderCartSections(lastResponse.sections);
      this.#showCadenceNotices();
      this.#updateQuantitySelectors(lastResponse);
      this.#updateCartQuantitySelectorButtonStates();
      this.#renderDrawerCount();
    }
  }

  /**
   * Morphs every cart-items section from a response's `sections`, or
   * re-fetches them when the response carried none.
   *
   * @param {Record<string, string> | undefined} sections
   */
  async #renderCartSections(sections) {
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (!(component instanceof CartItemsComponent)) continue;
      const mode = component.isDrawer ? 'hydration' : 'full';
      const html = sections?.[component.sectionId];
      if (html) await morphSection(component.sectionId, html, { mode });
      else await sectionRenderer.renderSection(component.sectionId, { cache: false, mode });
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#rowForLine(line);

    // Gifts ride on a subscription line. Removing the last subscription takes
    // them along in the same request and the same paint — otherwise they'd
    // hang around until gift-sync notices, two round trips later.
    const giftRows = cartItemRowToRemove ? this.#orphanedGiftRows(cartItemRowToRemove) : [];

    // A gift the shopper removes stays out: the decline is written to the
    // cart in the same request, and gift-sync leaves declined gifts alone
    // until the subscription they came with is gone
    const declined = cartItemRowToRemove ? this.#declinedGiftsWith(cartItemRowToRemove) : null;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      clearKeys: giftRows.map((row) => row.dataset.key ?? '').filter(Boolean),
      ...(declined === null ? {} : { attributes: { _declined_gifts: declined } }),
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...giftRows,
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    if (isEmptyCart && document.getElementById('empty-cart-template') instanceof HTMLTemplateElement) {
      startViewTransition(() => {
        this.#revertToEmpty();
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Gift rows that lose their qualifier when `row` is removed: every gift
   * line, but only when `row` is the last subscription line in the cart.
   *
   * @param {HTMLElement} row - The row about to be removed.
   * @returns {HTMLElement[]}
   */
  #orphanedGiftRows(row) {
    if (!row.classList.contains('cart-items__table-row--subscription')) return [];

    const rows = this.refs.cartItemRows ?? [];
    const hasOtherSubscription = rows.some(
      (other) =>
        other !== row &&
        other.classList.contains('cart-items__table-row--subscription') &&
        !other.classList.contains('removing')
    );
    if (hasOtherSubscription) return [];

    return rows.filter(
      (other) => other.classList.contains('cart-items__table-row--gift') && !other.classList.contains('removing')
    );
  }

  /**
   * The cart's declined gift list (`_declined_gifts`, as the form carries it)
   * with `row`'s variant added — or null when `row` is no gift row.
   *
   * @param {HTMLElement} row - The row about to be removed.
   * @returns {string | null} Comma separated variant ids.
   */
  #declinedGiftsWith(row) {
    if (!row.classList.contains('cart-items__table-row--gift')) return null;
    const variantId = row.dataset.variantId;
    if (!variantId) return null;

    const form = this.querySelector('.cart-form');
    const current = (form instanceof HTMLElement ? form.dataset.declinedGifts ?? '' : '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    return [...new Set([...current, variantId])].join(',');
  }

  /**
   * Handles a delivery frequency change on a subscription line.
   * The `<select>` lists the variant's selling plans; the line keeps its
   * quantity and moves to the chosen plan.
   * @param {number} line - The 1-based line index.
   * @param {Event} event - The change event.
   */
  onLineItemPlanChange(line, event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const sellingPlan = Number(select.value);
    const quantity = parseInt(select.dataset.quantity ?? '', 10) || 1;
    if (!Number.isFinite(sellingPlan) || sellingPlan <= 0) return;

    const row = this.#rowForLine(line);
    this.updateQuantity({
      line,
      quantity,
      sellingPlan,
      action: 'change',
      followUps: row ? this.#groupFollowUps(row, sellingPlan) : [],
    });
  }

  /**
   * Moves a one-time line onto a subscription — the "Subscribe & save"
   * offer under its row (see snippets/cart-item-upsell). The button carries
   * the plan the server resolved for the cart-wide item count at the
   * cadence to join; the card is re-rendered with every cart change, so it
   * is current when tapped. Split siblings of the line follow it in the
   * same request chain, and the reconcile after the response settles every
   * line's tier against what actually subscribes.
   *
   * @param {number} line - The 1-based line index.
   * @param {Event} event - The click event, whose target is the offer button.
   */
  onLineItemUpgrade(line, event) {
    const row = this.#rowForLine(line);
    const button = event.target instanceof HTMLElement ? event.target : null;
    if (!row || !button) return;

    const quantity = Number(row.dataset.lineQuantity) || 0;
    const sellingPlan = Number(button.dataset.upgradePlan) || 0;
    if (quantity <= 0 || !sellingPlan) return;

    this.updateQuantity({
      line,
      quantity,
      sellingPlan,
      action: 'change',
      followUps: this.#groupFollowUps(row, sellingPlan),
    });

    const textComponent = /** @type {TextComponent | undefined} */ (row.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {number} [config.sellingPlan] - A selling plan id to move the line to.
   * @param {string[]} [config.clearKeys] - Keys of extra lines to remove in the same request (e.g. orphaned gifts).
   * @param {Record<string, string>} [config.attributes] - Cart attributes to write in the same request (e.g. the declined gift list).
   * @param {Array<{ key: string, quantity: number, sellingPlan: number }>} [config.followUps] - Sibling lines to move to a plan right after, by key.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const sections = this.#cartSectionIds;
    const lineId = this.#rowForLine(line)?.dataset.key ?? '';
    const clearKeys = (config.clearKeys ?? []).filter((key) => key && key !== lineId);
    const followUps = (config.followUps ?? []).filter((followUp) => followUp.key && followUp.key !== lineId);

    // Several lines at once — or a cart attribute alongside the line — go
    // through /cart/update keyed by line key; a single line keeps the
    // upstream /cart/change call by line number.
    // Only the last request of the chain renders the sections.
    const batchClear = clearKeys.length > 0 && lineId !== '';
    const useUpdate = (batchClear || config.attributes !== undefined) && lineId !== '';
    /** @param {Record<string, unknown>} body */
    const withSections = (body) => ({ ...body, sections, sections_url: window.location.pathname });
    const primaryBody = useUpdate
      ? {
          updates: Object.fromEntries([[lineId, quantity], ...clearKeys.map((key) => [key, 0])]),
          ...(config.attributes ? { attributes: config.attributes } : {}),
        }
      : { line, quantity, ...(config.sellingPlan ? { selling_plan: config.sellingPlan } : {}) };
    const url = useUpdate ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
    /** @param {any} parsed */
    const cartErrors = (parsed) =>
      parsed.errors ?? (parsed.status ? parsed.description || parsed.message : undefined);

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }, ...(batchClear ? clearKeys.map((key) => ({ id: key, quantity: 0 })) : [])],
        promise: deferredUpdatePromise.promise,
      })
    );

    const request = async () => {
      let parsed = await this.#postCart(url, followUps.length ? primaryBody : withSections(primaryBody));
      const errors = cartErrors(parsed);
      if (errors) {
        resetShimmer(this);
        this.#handleCartError(line, { ...parsed, errors });
        deferredUpdatePromise.reject(new Error(errors));
        return null;
      }

      for (const [index, followUp] of followUps.entries()) {
        const body = { id: followUp.key, quantity: followUp.quantity, selling_plan: followUp.sellingPlan };
        const next = await this.#postCart(
          Theme.routes.cart_change_url,
          index === followUps.length - 1 ? withSections(body) : body
        );
        // A sibling the primary change already merged away is no failure;
        // a response without sections falls back to a section fetch below.
        if (!cartErrors(next)) parsed = next;
      }
      return parsed;
    };

    request()
      .then(async (parsedResponseText) => {
        if (!parsedResponseText) return;

        resetShimmer(this);

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHtml) parsedResponseText = await this.fetchCartData();

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = sectionHtml
          ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
          : String(parsedResponseText.item_count ?? '');
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            attributes: parsedResponseText.attributes,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        if (sectionHtml) {
          morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
        }

        this.#renderDrawerCount();
        this.#updateCartQuantitySelectorButtonStates();
        this.reconcilePlans();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.#rowForLine(line)?.querySelector('input[data-cart-line]');

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;

    if (event.action === 'add') {
      this.#optimisticAdd(event);
    }

    event.promise
      ?.then(async ({ detail }) => {
        this.removeAttribute('aria-busy');

        if (detail?.didError) {
          this.#revertOptimisticAdd(wasEmptyCartDrawer);
          return;
        }

        this.#optimisticFromEmpty = false;

        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer && !this.querySelector('[data-optimistic]')) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
              this.#renderDrawerCount();
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            this.#renderDrawerCount();
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
          this.reconcilePlans();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
          this.reconcilePlans();
        }
      })
      .catch((error) => {
        this.#revertOptimisticAdd(wasEmptyCartDrawer);
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Appends a loading-skeleton row per variant being added. The row shows no
   * product data: what the page knows (a pack total, a chip image) isn't the
   * line Shopify will return, so the response's real row fills the slot.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #optimisticAdd(event) {
    if (!this.isDrawer) return;

    const wasEmpty = this.querySelector('[data-cart-drawer-empty]') !== null;

    if (wasEmpty) {
      this.#swapToFilledShell();
      this.#optimisticFromEmpty = true;
      this.#previewShipping(event);
    } else {
      // The heading count already moved with the event; the subtotal can't be
      // known until the response.
      this.refs.cartTotal?.shimmer();
    }

    const list =
      this.querySelector('[data-optimistic-list]') ??
      this.querySelector('tbody[role="rowgroup"]') ??
      this.querySelector('tbody');
    const itemTemplate = document.getElementById('optimistic-cart-item-template');
    if (!list || !(itemTemplate instanceof HTMLTemplateElement)) return;

    this.setAttribute('aria-busy', 'true');

    const variantIds = new Set();
    for (const line of event.lines ?? []) {
      if ((Number(line.quantity) || 0) > 0) variantIds.add(String(line.merchandiseId ?? ''));
    }
    // An add the event can't describe still fills the empty shell with one row
    if (variantIds.size === 0 && this.#optimisticFromEmpty) variantIds.add('');

    for (const variantId of variantIds) {
      // A second add of a variant already pending reuses its row
      if (variantId && list.querySelector(`[data-optimistic-variant="${CSS.escape(variantId)}"]`)) continue;

      const row = itemTemplate.content.firstElementChild?.cloneNode(true);
      if (!(row instanceof HTMLTableRowElement)) continue;

      if (variantId) row.dataset.optimisticVariant = variantId;
      list.append(row);
    }
  }

  /**
   * A subscription line unlocks free shipping outright (see cart-shipping-bar),
   * so when the add carries a selling plan the shell can show the unlocked bar
   * and the free-shipping total immediately. A one-time add shows the bar as a
   * shimmer instead: how far the cart is from the free-shipping threshold is
   * only known once the response has the cart total, and the shipping line
   * stays a shimmer for the same reason. With no threshold set, a one-time
   * add shows no bar at all.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #previewShipping(event) {
    const source = event.target instanceof Element ? event.target : null;
    const planInput = source?.querySelector('input[name="selling_plan"]');
    const hasSubscription = planInput instanceof HTMLInputElement && planInput.value !== '';
    const bar = this.querySelector('[data-optimistic-shipping]');

    if (!hasSubscription) {
      if (bar instanceof HTMLElement && bar.dataset.optimisticShipping === 'threshold') bar.removeAttribute('hidden');
      return;
    }

    bar?.removeAttribute('hidden');
    const fill = bar?.querySelector('.cart-shipping-bar__fill');
    if (fill instanceof HTMLElement) fill.style.width = '100%';
    for (const pending of this.querySelectorAll('[data-optimistic-shipping-pending]')) {
      pending.setAttribute('hidden', '');
    }
    for (const unlocked of this.querySelectorAll('[data-optimistic-shipping-unlocked]')) {
      unlocked.removeAttribute('hidden');
    }
  }

  #swapToFilledShell() {
    const inner = this.#drawerInner();
    const bodyTemplate = document.getElementById('optimistic-cart-body-template');
    if (!(bodyTemplate instanceof HTMLTemplateElement)) return;

    inner?.classList.remove('cart-drawer--empty');
    this.#replaceDrawerHeader('filled-cart-header-template');
    this.replaceChildren(bodyTemplate.content.cloneNode(true));
    this.#renderDrawerCount();
    // The empty state leaves the dialog in "unsticky" mode; pin the shell's
    // summary to the bottom now so it doesn't jump when the response lands.
    this.closest('dialog')?.setAttribute('cart-summary-sticky', 'true');
  }

  #revertToEmpty() {
    const bodyTemplate = document.getElementById('empty-cart-template');
    if (!(bodyTemplate instanceof HTMLTemplateElement)) return;

    this.#drawerInner()?.classList.add('cart-drawer--empty');
    // The empty header carries the drawer title (and the dialog's name)
    // like the filled one, so the swap replaces the heading along with it.
    this.#replaceDrawerHeader('empty-cart-header-template');
    this.replaceChildren(bodyTemplate.content.cloneNode(true));
    this.#optimisticFromEmpty = false;
    this.removeAttribute('aria-busy');
  }

  /** @param {boolean} wasEmpty */
  #revertOptimisticAdd(wasEmpty) {
    this.removeAttribute('aria-busy');
    if (wasEmpty || this.#optimisticFromEmpty) {
      this.#revertToEmpty();
      return;
    }

    for (const row of this.querySelectorAll('[data-optimistic]')) {
      row.remove();
    }
  }

  /** @param {string} templateId */
  #replaceDrawerHeader(templateId) {
    const inner = this.#drawerInner();
    const template = document.getElementById(templateId);
    if (!inner || !(template instanceof HTMLTemplateElement)) return;

    const current = inner.querySelector(':scope > .theme-drawer__header');
    const next = template.content.cloneNode(true);
    if (current) current.replaceWith(next);
    else inner.prepend(next);
  }

  #drawerInner() {
    return this.closest('.cart-drawer__inner');
  }

  /**
   * The count the server rendered into the drawer heading, used to seed the
   * optimistic count before any request is made.
   * @returns {number}
   */
  #renderedDrawerCount() {
    const text = this.#drawerInner()?.querySelector('.cart-drawer__count')?.textContent ?? '';
    return parseInt(text.replace(/\D/g, ''), 10) || 0;
  }

  /**
   * Writes the optimistic count into the drawer heading. Runs on every count
   * change and again after each morph, since the server markup only knows the
   * count as of its own request while other requests may still be in flight.
   */
  #renderDrawerCount = () => {
    const inner = this.#drawerInner();
    const badge = inner?.querySelector('.cart-drawer__count');
    if (!(badge instanceof HTMLElement)) return;

    const count = cartCount.value;
    badge.textContent = count > 0 ? `(${count})` : '';
    badge.classList.toggle('visually-hidden', count === 0);

    const label = inner?.querySelector('[data-cart-count-label]');
    if (label) label.textContent = count > 0 ? `${Theme.translations.cart_count}: ${count}` : '';
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
